import { AlertTriangle, Ban, Eye, Info, Pause } from 'lucide-react';
import { Fragment } from 'react';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';
import { formatAuditTimestamp } from '~/utils/date';

import type { BatchRun, BatchRuns, NeedsAttention } from '../../types/batch-retry.api.types';
import type { BatchRetryState } from '../../utils/batch-retry-state';
import { runTotal } from '../batch-retry/batch-retry-utils';

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

function runDotClass(run: BatchRun): string {
  if (run.kind === 'ROOT') return 'bg-warning';
  if (run.status === 'COMPLETED') return 'bg-success';
  if (run.status === 'FAILED') return 'bg-error';
  if (run.status === 'TERMINATED') return 'bg-neutral-400';
  return 'bg-info';
}

interface BatchRunChipsProps {
  runs: BatchRuns;
  /** Selects that run in the Runs tab (the original run included). */
  onSelectRun: (runId: string | null) => void;
}

/** Original → Retry #1 (+ok) → … → "n excluded". Each chip jumps to the Runs tab. */
export function BatchRunChips({ runs, onSelectRun }: BatchRunChipsProps) {
  const chipClass =
    'hover:bg-muted inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors';
  return (
    <div data-testid="batch-run-chips" className="flex flex-wrap items-center gap-1.5">
      <span className="text-muted-foreground text-xs">Runs:</span>
      {runs.runs.map((run, i) => (
        <Fragment key={run.runId}>
          {i > 0 && (
            <span className="text-muted-foreground" aria-hidden="true">
              ›
            </span>
          )}
          <button
            type="button"
            data-testid={`batch-run-chip-${run.kind === 'ROOT' ? 'root' : String(run.attempt)}`}
            className={chipClass}
            onClick={() => {
              onSelectRun(run.runId);
            }}
          >
            <span className={cn('h-2 w-2 rounded-full', runDotClass(run))} aria-hidden="true" />
            {run.kind === 'ROOT' ? 'Original' : `Retry #${String(run.attempt)}`}
            {run.kind === 'RETRY' && run.status === 'COMPLETED' && run.succeeded !== null && (
              <span className="text-success tabular-nums">+{run.succeeded.toLocaleString()}</span>
            )}
          </button>
        </Fragment>
      ))}
      {runs.counts.excluded > 0 && (
        <>
          <span className="text-muted-foreground" aria-hidden="true">
            ›
          </span>
          <button
            type="button"
            data-testid="batch-run-chip-excluded"
            className={chipClass}
            onClick={() => {
              // Excluded products are the original run's leftovers: show that run.
              onSelectRun(runs.runs.find((r) => r.kind === 'ROOT')?.runId ?? null);
            }}
          >
            <span className="h-2 w-2 rounded-full bg-neutral-400" aria-hidden="true" />
            {runs.counts.excluded.toLocaleString()} excluded
          </button>
        </>
      )}
    </div>
  );
}

function reasonSummary(needsAttention: NeedsAttention | undefined, notAttempted: number): string {
  const parts: string[] = [];
  for (const bucket of needsAttention?.buckets ?? []) {
    if (bucket.code === 'FLAGGED_FOR_REVIEW' || bucket.code === 'NOT_ATTEMPTED') continue;
    for (const r of bucket.reasons) {
      if (r.count > 0) parts.push(`${r.label} ${r.count.toLocaleString()}`);
    }
  }
  if (notAttempted > 0) parts.push(`not reached ${notAttempted.toLocaleString()}`);
  return parts.join(' · ');
}

interface BatchRetryBannerProps {
  runs: BatchRuns;
  state: BatchRetryState;
  needsAttention: NeedsAttention | undefined;
  onViewProducts: () => void;
  onExclude: () => void;
  onUndoExclusion: () => void;
  undoPending: boolean;
  /** The running retry is parked at its cost gate (its estimate is PENDING_APPROVAL). */
  retryAwaitingCostApproval?: boolean;
}

/** The one banner that explains where the batch stands after (or during) retries. */
export function BatchRetryBanner({
  runs,
  state,
  needsAttention,
  onViewProducts,
  onExclude,
  onUndoExclusion,
  undoPending,
  retryAwaitingCostApproval = false,
}: BatchRetryBannerProps) {
  switch (state.banner) {
    case 'not-enriched':
      return (
        <div
          data-testid="batch-retry-banner-not-enriched"
          className="bg-warning-50 border-warning/30 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-warning h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              <b>
                {state.left.toLocaleString()} {plural(state.left, 'product is', 'products are')} not
                enriched yet
              </b>
              <span className="text-muted-foreground ml-2">
                {[
                  state.failedRetryable > 0
                    ? `${state.failedRetryable.toLocaleString()} failed (retryable)`
                    : '',
                  state.failedPermanent > 0
                    ? `${state.failedPermanent.toLocaleString()} failed (permanent)`
                    : '',
                  state.notAttempted > 0
                    ? `${state.notAttempted.toLocaleString()} never attempted`
                    : '',
                  `${String(state.attemptsLeft)} of ${String(state.maxAttempts)} retries left`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
          </div>
          <Button
            variant="link"
            className="h-auto cursor-pointer p-0 text-sm"
            onClick={onViewProducts}
          >
            See products and reasons ›
          </Button>
        </div>
      );
    case 'stuck':
      return (
        <StuckPanel
          state={state}
          needsAttention={needsAttention}
          onViewProducts={onViewProducts}
          onExclude={onExclude}
          canExclude={state.has('EXCLUDE_REMAINING')}
        />
      );
    case 'excluded':
      return (
        <div
          data-testid="batch-retry-banner-excluded"
          className="bg-muted/40 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm"
        >
          <span>
            <b>
              {state.excluded.toLocaleString()} {plural(state.excluded, 'product', 'products')}{' '}
              excluded from this batch
            </b>
            <span className="text-muted-foreground ml-1">
              {runs.exclusion?.excludedBy ? `by ${runs.exclusion.excludedBy} · ` : ''}
              they won&apos;t be delivered with this batch and stay in the catalog for the next run
            </span>
          </span>
          {state.has('UNDO_EXCLUSION') && (
            <Button
              data-testid="batch-retry-undo-exclusion"
              variant="link"
              className="h-auto cursor-pointer p-0 text-sm"
              isLoading={undoPending}
              onClick={onUndoExclusion}
            >
              Undo
            </Button>
          )}
        </div>
      );
    case 'retrying':
      if (retryAwaitingCostApproval) {
        return (
          <div
            data-testid="batch-retry-banner-retrying-cost"
            className="bg-warning-50 border-warning/30 flex items-center gap-2 rounded-lg border px-4 py-3 text-sm"
          >
            <Pause className="text-warning h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              <b>
                Retry #{state.activeRun?.attempt ?? state.attemptsUsed} is waiting for cost approval
              </b>
              <span className="text-muted-foreground">
                {' '}
                · {(state.activeRun ? runTotal(state.activeRun) : state.left).toLocaleString()}{' '}
                products · enrichment starts once an approver approves it
              </span>
            </span>
          </div>
        );
      }
      return (
        <div
          data-testid="batch-retry-banner-retrying"
          className="bg-info-50 border-info/30 flex items-center gap-2 rounded-lg border px-4 py-3 text-sm"
        >
          <Info className="text-info h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            <b>
              Retry #{state.activeRun?.attempt ?? state.attemptsUsed} is enriching{' '}
              {(state.activeRun ? runTotal(state.activeRun) : state.left).toLocaleString()} products
            </b>
            {state.activeRun?.done != null && (
              <span data-testid="batch-retry-progress">
                {' '}
                · {state.activeRun.done.toLocaleString()} done so far
              </span>
            )}
            <span className="text-muted-foreground"> · Review unlocks when it finishes</span>
          </span>
        </div>
      );
    case 'paused':
      return (
        <div
          data-testid="batch-retry-banner-paused"
          className="bg-muted/40 flex items-center gap-2 rounded-lg border px-4 py-3 text-sm"
        >
          <Pause className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            <b>
              Paused
              {runs.pausedBy ? ` by ${runs.pausedBy}` : ''}
              {runs.pausedAt ? ` · ${formatAuditTimestamp(runs.pausedAt)}` : ''}
            </b>
            <span className="text-muted-foreground">
              {' '}
              · batches already in flight finished; nothing new starts until you resume
            </span>
          </span>
        </div>
      );
    default:
      return null;
  }
}

interface StuckPanelProps {
  state: BatchRetryState;
  needsAttention: NeedsAttention | undefined;
  onViewProducts: () => void;
  onExclude: () => void;
  /** Server-decided: EXCLUDE_REMAINING is in `availableActions`. */
  canExclude: boolean;
}

/** Retries used up, products left: say why, and offer to approve without them or exclude them. */
export function StuckPanel({
  state,
  needsAttention,
  onViewProducts,
  onExclude,
  canExclude,
}: StuckPanelProps) {
  const summary = reasonSummary(needsAttention, state.notAttempted);
  const retriesUsedUp = state.attemptsLeft === 0;
  return (
    <div
      data-testid="batch-retry-stuck-panel"
      className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-orange-300 bg-orange-50 px-4 py-3 text-sm"
    >
      <div className="flex min-w-[260px] flex-1 flex-col gap-1.5">
        <span>
          <b className="text-orange-800">
            {state.left.toLocaleString()} {plural(state.left, 'product', 'products')} could not be
            enriched
            {retriesUsedUp
              ? ` after ${String(state.maxAttempts)} retries`
              : ' and cannot be retried'}
          </b>
          {summary !== '' && <span className="text-muted-foreground"> · {summary}</span>}
        </span>
        <span className="text-muted-foreground text-xs">
          {retriesUsedUp
            ? 'Retries are used up. '
            : 'No retry can pick these up (permanent failures need their source data fixed). '}
          You can approve the batch without them, or exclude them from this batch so they stop
          showing as issues. Excluded products stay in your catalog unenriched; the next run picks
          them up again.
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          data-testid="batch-retry-stuck-see-why"
          variant="outline"
          size="sm"
          className="cursor-pointer"
          onClick={onViewProducts}
        >
          <Eye aria-hidden="true" /> See why
        </Button>
        {canExclude && (
          <Button
            data-testid="batch-retry-stuck-exclude"
            variant="outline"
            size="sm"
            className="cursor-pointer"
            onClick={onExclude}
          >
            <Ban aria-hidden="true" /> Exclude from this batch
          </Button>
        )}
      </div>
    </div>
  );
}
